import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const BrandContext = createContext(null);

export const BrandProvider = ({ children }) => {
  const { user, viewAs } = useAuth();
  const brandStorage = viewAs ? sessionStorage : localStorage;
  const brandKey = viewAs ? 'clientViewAsBrandId' : 'currentBrandId';
  const [brands, setBrands] = useState([]);
  const [currentBrand, setCurrentBrand] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load brands when user logs in
  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'organizer')) {
      fetchBrands();
    } else {
      setBrands([]);
      setCurrentBrand(null);
      setLoading(false);
    }
  }, [user]);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await api.get('/brands');
      const brandList = response.data.data || [];
      setBrands(brandList);

      // Restore last selected brand from localStorage or use first brand
      const savedBrandId = brandStorage.getItem(brandKey);
      const savedBrand = brandList.find(b => b._id === savedBrandId);

      if (savedBrand) {
        setCurrentBrand(savedBrand);
      } else if (brandList.length > 0) {
        setCurrentBrand(brandList[0]);
        brandStorage.setItem(brandKey, brandList[0]._id);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const switchBrand = (brandId) => {
    if (brandId === null) {
      // "All Brands" selected
      setCurrentBrand(null);
      brandStorage.removeItem(brandKey);
    } else {
      const brand = brands.find(b => b._id === brandId);
      if (brand) {
        setCurrentBrand(brand);
        brandStorage.setItem(brandKey, brandId);
      }
    }
  };

  const createBrand = async (brandData) => {
    const response = await api.post('/brands', brandData);
    const newBrand = response.data.data;
    setBrands(prev => [...prev, newBrand]);

    // If this is the first brand, set it as current
    if (!currentBrand) {
      setCurrentBrand(newBrand);
      brandStorage.setItem(brandKey, newBrand._id);
    }

    return newBrand;
  };

  const updateBrand = async (brandId, brandData) => {
    const response = await api.put(`/brands/${brandId}`, brandData);
    const updatedBrand = response.data.data;

    setBrands(prev => prev.map(b => b._id === brandId ? updatedBrand : b));

    if (currentBrand?._id === brandId) {
      setCurrentBrand(updatedBrand);
    }

    return updatedBrand;
  };

  const deleteBrand = async (brandId) => {
    await api.delete(`/brands/${brandId}`);
    setBrands(prev => prev.filter(b => b._id !== brandId));

    if (currentBrand?._id === brandId) {
      const remaining = brands.filter(b => b._id !== brandId);
      if (remaining.length > 0) {
        setCurrentBrand(remaining[0]);
        brandStorage.setItem(brandKey, remaining[0]._id);
      } else {
        setCurrentBrand(null);
        brandStorage.removeItem(brandKey);
      }
    }
  };

  const refreshBrands = () => {
    fetchBrands();
  };

  return (
    <BrandContext.Provider value={{
      brands,
      currentBrand,
      loading,
      switchBrand,
      createBrand,
      updateBrand,
      deleteBrand,
      refreshBrands,
      hasBrands: brands.length > 0
    }}>
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = () => useContext(BrandContext);
